import { Button, Intent } from "@blueprintjs/core";
import { IconNames } from "@blueprintjs/icons";
import { IDataTableColumn, ISearchCriteriaValues, ToolTipButton, SortKeysObject } from "nsitools-react";
import * as React from "react";
import styled from "styled-components";

import { BodyContainer, ButtonsColumn, ColumnsContainer } from "../..";
import { useTl } from "../../../hooks";
import { ETLCodes } from "../../../locales";
import { useDialog } from "../../../contexts";
import { SRSHCustomGridSelector } from "./SRSHCustomGridSelector";
import { SearchTablePage } from "../../searchTablePage";
import { EPlatform, FileResponse } from "../../../api";
export interface ISRSHCustomSelectorSelectedSearchIdsProps {
  selectedIds: any[];
}

export interface ISRSHCustomSelectorAvailableSearchIdsProps {
  availableIds: any[];
}
const SelectorContainer = styled.div`
  flex: 1;
`;
export interface ICustomSelectorProps<TResults> {
  linkedIds: number[];
  onLinkedIdsChanged: (ids: number[]) => void;
  searchFunction: (sObj: ISearchCriteriaValues) => TResults | Promise<TResults>;
  searchIdsFunc: (sObj: any) => any[] | Promise<any[]>;
  exportFunction?: (sObj: ISearchCriteriaValues) => Promise<FileResponse>;
  columns: Array<IDataTableColumn>;
  idField: string;
  errorFields?: string[];
  editMode: boolean;
  sortKeys?: SortKeysObject;
  customizeRowStyle?: any;
  isErrorSelected?: (isError: boolean) => void;
  platform?: EPlatform;
  showAllButtons?: boolean;
}

export function SRSHCustomSelector<TResults>(props: ICustomSelectorProps<TResults>) {
  const {
    linkedIds = [],
    searchFunction,
    searchIdsFunc,
    columns,
    exportFunction,
    idField,
    editMode,
    onLinkedIdsChanged,
    errorFields,
    isErrorSelected,
    sortKeys = {},
    platform = EPlatform.CARE4DOGS,
    showAllButtons = true,
  } = props;
  const { t } = useTl();

  const [availableSearchObject, setAvailableSearchObject] = React.useState<any>();
  const [selectedSearchObject, setSelectedSearchObject] = React.useState<any>();

  const availableSearchFunction = React.useCallback(
    (sObj: any) => {
      const currentSObj = {
        ...sObj,
        selectedIds: linkedIds,
        excludeSelectedIds: true,
      };
      setAvailableSearchObject(currentSObj);
      return searchFunction(currentSObj);
    },
    [linkedIds, searchFunction]
  );
  const selectedSearchFunction = React.useCallback(
    (sObj: any) => {
      const currentSObj = {
        ...sObj,
        selectedIds: linkedIds,
        excludeSelectedIds: false,
      };
      setSelectedSearchObject(currentSObj);
      return searchFunction(currentSObj);
    },
    [linkedIds, searchFunction]
  );

  const exportSelectedFunction = React.useCallback(
    (sObj: any) => {
      const currentSObj = {
        ...sObj,
        selectedIds: linkedIds,
        excludeSelectedIds: false,
      };
      setSelectedSearchObject(currentSObj);
      return exportFunction(currentSObj);
    },
    [exportFunction, linkedIds]
  );

  const [leftSelected, setLeftSelected] = React.useState<any[]>([]);
  const [rightSelected, setRightSelected] = React.useState<any[]>([]);

  const [addAllLoading, setAddAllLoading] = React.useState(false);

  const { showDialog } = useDialog();

  const addAll = React.useCallback(async () => {
    try {
      setAddAllLoading(true);
      let ids: any[] = [];
      ids = await searchIdsFunc({
        availableSearchObject,
        forceSkip: 0,
        forceTake: 0,
      });

      showDialog({
        title: t(ETLCodes.AddAll),
        message: t(ETLCodes.AddAllText, { count: ids.length }),
        onConfirmed: async () => {
          setLeftSelected([]);
          onLinkedIdsChanged(Array.from(new Set([...ids, ...linkedIds])));
        },
      });
    } catch (error) {}
    setAddAllLoading(false);
  }, [availableSearchObject, linkedIds, onLinkedIdsChanged, searchIdsFunc, showDialog, t]);

  const addSelected = React.useCallback(() => {
    onLinkedIdsChanged(Array.from(new Set([...linkedIds, ...leftSelected])));
    setRightSelected([]);
    setLeftSelected([]);
  }, [leftSelected, linkedIds, onLinkedIdsChanged]);

  const [removeAllLoading, setRemoveAllLoading] = React.useState(false);

  const removeAll = React.useCallback(async () => {
    try {
      setRemoveAllLoading(true);
      let ids: any[] = [];
      ids = await searchIdsFunc({
        ...selectedSearchObject,
        forceSkip: 0,
        forceTake: 0,
      });
      showDialog({
        title: t(ETLCodes.RemoveAll),
        message: t(ETLCodes.RemoveAllText, { count: ids.length }),
        onConfirmed: async () => {
          setRightSelected([]);
          onLinkedIdsChanged(linkedIds.filter((id) => !ids.includes(id)));
        },
      });
    } catch (error) {}
    setRemoveAllLoading(false);
  }, [linkedIds, onLinkedIdsChanged, searchIdsFunc, selectedSearchObject, showDialog, t]);

  const removeSelected = React.useCallback(() => {
    onLinkedIdsChanged(linkedIds.filter((id) => !rightSelected.includes(id)));
    setLeftSelected([]);
    setRightSelected([]);
  }, [linkedIds, onLinkedIdsChanged, rightSelected]);

  const [leftCount, setLeftCount] = React.useState(0);
  const [rightCount, setRightCount] = React.useState(0);

  return editMode ? (
    <>
      <BodyContainer>
        <ColumnsContainer>
          <SelectorContainer>
            <SRSHCustomGridSelector
              idField={idField}
              errorFields={errorFields}
              searchFunction={availableSearchFunction}
              selectionChanged={setLeftSelected}
              selectedIds={leftSelected}
              currentCountChanged={setLeftCount}
              columns={columns}
              title={t(ETLCodes.SelectorAvailable)}
              sortKeys={sortKeys}
            />
          </SelectorContainer>
          <ButtonsColumn>
            {showAllButtons && (
              <Button
                loading={addAllLoading}
                intent={Intent.PRIMARY}
                icon={IconNames.DOUBLE_CHEVRON_RIGHT}
                onClick={addAll}
                disabled={leftCount === 0}
              />
            )}
            <ToolTipButton
              disabled={leftSelected.length === 0}
              tooltip={t(ETLCodes.AddElementText, {
                count: leftSelected.length,
              })}
              intent={Intent.PRIMARY}
              icon={IconNames.CHEVRON_RIGHT}
              onClick={addSelected}
            />
            <ToolTipButton
              disabled={rightSelected.length === 0}
              tooltip={t(ETLCodes.RemoveElementText, {
                count: rightSelected.length,
              })}
              intent={Intent.PRIMARY}
              icon={IconNames.CHEVRON_LEFT}
              onClick={removeSelected}
            />
            {showAllButtons && (
              <Button
                disabled={rightCount === 0}
                loading={removeAllLoading}
                intent={Intent.PRIMARY}
                icon={IconNames.DOUBLE_CHEVRON_LEFT}
                onClick={removeAll}
              />
            )}
          </ButtonsColumn>
          <SelectorContainer>
            <SRSHCustomGridSelector
              idField={idField}
              errorFields={errorFields}
              searchFunction={selectedSearchFunction}
              isErrorSelected={isErrorSelected}
              selectionChanged={setRightSelected}
              selectedIds={rightSelected}
              currentCountChanged={setRightCount}
              columns={columns}
              title={t(ETLCodes.SelectorSelected)}
              sortKeys={sortKeys}
            />
          </SelectorContainer>
        </ColumnsContainer>
      </BodyContainer>
    </>
  ) : (
    <SearchTablePage
      platform={platform}
      columns={columns}
      searchFunction={selectedSearchFunction}
      exportFunction={exportFunction ? exportSelectedFunction : undefined}
      withCard={false}
    />
  );
}
